// NirmalX pages style their own links (CSS modules), so they use next/link rather than the Chakra-styled Link.
// eslint-disable-next-line no-restricted-imports
import NextLink from 'next/link';
import React from 'react';

import type { Block } from 'types/api/block';

import config from 'configs/app';
import SearchBar from 'ui/snippets/searchBar/SearchBar';

import { ago, findValidator, shortHash, VALIDATORS } from '../nirmalx';
import css from './home.module.css';

// Embers drifting up from the horizon. Fixed values (no Math.random) so server and client render alike.
const EMBERS = Array.from({ length: 22 }, (_, i) => ({
  '--x': `${ (i * 47 + 7) % 100 }%`,
  '--s': `${ 2 + (i % 3) }px`,
  '--d': `${ 7 + (i % 5) * 1.7 }s`,
  '--delay': `${ (i * 0.83) % 9 }s`,
  '--drift': `${ ((i % 7) - 3) * 22 }px`,
}) as React.CSSProperties);

interface Props {
  latest: Block | undefined;
  now: number;
}

const Hero = ({ latest, now }: Props) => {
  const proposer = findValidator(latest?.miner.hash);

  return (
    <section className={ css.hero }>
      <div className={ css.sky }/>
      <div className={ css.rays }/>
      <div className={ css.sun }/>
      <div className={ css.floor }><div className={ css.grid }/></div>
      <div className={ css.horizon }/>
      { EMBERS.map((style, index) => <span key={ index } className={ css.ember } style={ style }/>) }

      <div className={ css.heroContent }>
        <span className={ css.liveChip }><span className={ css.dot }/>Live · { config.chain.name } · Chain { config.chain.id }</span>
        <h1 className={ css.heroTitle }>Explore the rise<br/>of NirmalX.</h1>
        <p className={ css.heroSub }>
          Search any block, transaction, address or token on the { config.chain.name }, sealed live by its QBFT validators.
        </p>
        <div className={ css.searchFrame }>
          <SearchBar isHomepage/>
        </div>
        <div className={ css.pulseRow }>
          { latest && now > 0 && (
            <div className={ css.pulse }>
              <span className={ css.dot }/>
              Block
              <NextLink href={{ pathname: '/block/[height_or_hash]', query: { height_or_hash: String(latest.height) } }}>
                #{ latest.height.toLocaleString('en-US') }
              </NextLink>
              sealed { ago(latest.timestamp, now) } by { proposer?.name ?? shortHash(latest.miner.hash) }
            </div>
          ) }
        </div>
        <div className={ css.chips }>
          <span className={ css.chip }>QBFT consensus</span>
          <span className={ css.chip }>{ VALIDATORS.length } validators</span>
          <span className={ css.chip }>Chain ID { config.chain.id }</span>
          <span className={ css.chip }>Native coin { config.chain.currency.symbol }</span>
        </div>
      </div>
    </section>
  );
};

export default React.memo(Hero);
